import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Share2, Download } from "lucide-react";
import { PRODUCTS, BRAND, CATEGORIES } from "../data/collection";
import { buildOrderMessage, openOrderDM, openOrderWA, buildGeneralMessage, waLink, IG_DM } from "../lib/order";
import { WhatsAppMark, ReplyNudge } from "../components/site/QuickChat";
import { Reveal, MaskLine } from "../components/site/Reveal";

function CatalogueItem({ product, i }) {
  const flip = i % 2 === 1;

  const order = async () => {
    const copied = await openOrderDM(buildOrderMessage(product, null));
    toast[copied ? "success" : "message"](
      copied ? "Order details copied" : "Opening Instagram DM",
      { description: copied ? "Paste it into the chat that opened." : `Mention "${product.name}".` }
    );
  };

  const orderWA = () => {
    openOrderWA(buildOrderMessage(product, null));
    toast.success("Opening WhatsApp", { description: `Your ${product.name} message is prefilled.` });
  };

  const downloadCover = () => {
    const a = document.createElement("a");
    a.href = `/covers/${product.id}.jpg`;
    a.download = `Keilani-${product.name}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Cover downloaded", { description: "Share it on WhatsApp or Instagram." });
  };

  return (
    <Reveal>
      <article
        data-testid={`catalogue-item-${product.id}`}
        className="grid gap-8 border-t border-black/10 py-14 md:grid-cols-2 md:gap-16 md:py-20 print:break-inside-avoid"
      >
        {/* media */}
        <div className={flip ? "md:order-2" : ""}>
          {product.video && (
            <div className="relative mx-auto w-full max-w-[420px] overflow-hidden bg-black print:hidden">
              <video
                data-testid={`catalogue-video-${product.id}`}
                src={product.video}
                poster={product.cover}
                controls
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full object-contain"
              />
            </div>
          )}
          <div className="mt-3 grid grid-cols-3 gap-3">
            {product.gallery.map((src, k) => (
              <div key={k} className="overflow-hidden bg-neutral-100">
                <img src={src} alt={`${product.name} ${k + 1}`} className="aspect-[3/4] w-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* text */}
        <div className={`flex flex-col justify-center ${flip ? "md:order-1" : ""}`}>
          {product.badge && (
            <span
              data-testid={`catalogue-ribbon-${product.id}`}
              className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-[var(--kln-gold)]/50 bg-[var(--kln-gold)]/10 px-3 py-1 font-body text-[10px] uppercase tracking-[0.22em] text-[var(--kln-gold)]"
            >
              <span className="text-[10px] leading-none">&#10022;</span>
              {product.badge}
            </span>
          )}
          <span className="font-serif-editorial text-4xl font-light italic text-black/25">
            {product.index}
          </span>
          <h3 className="mt-2 font-display text-5xl md:text-6xl font-light leading-none">
            {product.name}
          </h3>
          <p className="mt-2 font-serif-editorial text-xl italic text-black/60">
            {product.meaning}
          </p>
          {product.verse && (
            <p className="mt-1 font-serif-editorial text-base italic text-[var(--kln-gold)]/85">
              &mdash; {product.verse}
            </p>
          )}
          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-display text-3xl font-light">
              &#8377;{product.price.toLocaleString("en-IN")}
            </span>
            <span className="font-body text-[11px] uppercase tracking-[0.18em] text-black/40">
              {product.priceNote}
            </span>
          </div>
          <p className="mt-5 max-w-md font-body text-sm md:text-base font-light leading-relaxed text-black/65">
            {product.description}
          </p>
          {product.colours && (
            <p className="mt-3 max-w-md font-serif-editorial text-base italic text-[var(--kln-gold)]">
              {product.colours}
            </p>
          )}
          <ul className="mt-6 space-y-2">
            {product.features.map((f) => (
              <li key={f} className="flex items-center gap-3 font-body text-sm font-light text-black/60">
                <span className="text-[var(--kln-gold)] text-xs leading-none">&#10022;</span>
                {f}
              </li>
            ))}
          </ul>
          <p className="mt-5 font-body text-[11px] uppercase tracking-[0.2em] text-black/40">
            Sizes XS — 4XL · Custom fit on request
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid={`catalogue-order-wa-${product.id}`}
              onClick={orderWA}
              className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 font-body text-[12px] uppercase tracking-[0.22em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px] print:hidden"
            >
              <WhatsAppMark className="h-4 w-4" />
              WhatsApp
            </button>
            <button
              data-testid={`catalogue-order-${product.id}`}
              onClick={order}
              className="group inline-flex items-center gap-3 rounded-full bg-[#050505] px-7 py-4 font-body text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-[var(--kln-gold)] hover:text-black print:hidden"
            >
              Instagram DM
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
            </button>
          </div>
          <button
            data-testid={`catalogue-cover-${product.id}`}
            onClick={downloadCover}
            className="group mt-4 inline-flex w-fit items-center gap-2.5 font-body text-[11px] uppercase tracking-[0.22em] text-black/55 transition-colors duration-300 hover:text-black print:hidden"
          >
            <Download className="h-4 w-4" strokeWidth={1.3} />
            <span className="relative">
              Download cover
              <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-black transition-transform duration-500 group-hover:scale-x-100" />
            </span>
          </button>
        </div>
      </article>
    </Reveal>
  );
}

export default function Catalogue() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const category = params.get("c") === "everyday" ? "everyday" : params.get("c") === "bloom" ? "bloom" : "onam";
  const cat = CATEGORIES[category];
  const items = PRODUCTS.filter((p) => p.category === category);
  const setCategory = (id) => {
    navigate(`/catalogue?c=${id}`);
  };

  const share = async () => {
    const url = `${window.location.origin}/catalogue?c=${category}`;
    const payload = {
      title: `Keilani Clothing · ${cat.label}`,
      text: `Keilani's ${cat.label} — take a look:`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (e) {
        /* user cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Catalogue link copied", { description: "Share it with anyone." });
    } catch (e) {
      toast.message("Share this link", { description: url });
    }
  };

  const downloadCover = () => {
    const a = document.createElement("a");
    a.href = `/catalogue-cover-${category}.jpg`;
    a.download = `Keilani-${cat.label.replace(/\s+/g, "-")}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Cover downloaded", { description: "Share it on WhatsApp or Instagram." });
  };

  return (
    <div data-testid="catalogue-page" className="bg-white text-[#050505]">
      {/* header */}
      <section className="relative overflow-hidden bg-[#050505] text-white">
        <div className="kln-noise" />
        <div className="relative mx-auto max-w-[1300px] px-6 md:px-12 pt-36 pb-20 md:pt-44 md:pb-28">
          <img
            src="/keilani-logo-white.png"
            alt="Keilani Clothing"
            className="mb-8 hidden h-5 w-auto print:block"
          />
          <button
            data-testid="catalogue-back"
            onClick={() => navigate("/")}
            className="group mb-10 flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-white/60 transition-colors hover:text-white print:hidden"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.3} />
            Back home
          </button>

          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <p className="mb-5 font-body text-[11px] uppercase tracking-[0.4em] text-white/50">
                <span className="text-[var(--kln-gold)]">Catalogue</span> · {cat.label}
              </p>
              <h1 className="font-display font-light leading-[0.9]" data-testid="catalogue-heading">
                <MaskLine delay={0.1} key={`h1-${category}`} className="text-6xl md:text-8xl">
                  {cat.headingA}
                </MaskLine>
                <MaskLine delay={0.24} key={`h2-${category}`} className="text-5xl md:text-7xl font-serif-editorial italic text-white/70">
                  {cat.headingB}
                </MaskLine>
              </h1>
              <p className="mt-8 max-w-xl font-serif-editorial text-xl md:text-2xl font-light italic text-white/70">
                {cat.caption}
              </p>
              <p className="mt-4 max-w-xl font-body text-sm font-light leading-relaxed text-white/50">
                {cat.body}
              </p>

              {/* Bloom / Onam / Everyday toggle */}
              <div
                data-testid="catalogue-toggle"
                className="mt-8 inline-flex rounded-full border border-white/20 bg-white/5 p-1"
              >
                {[CATEGORIES.bloom, CATEGORIES.onam, CATEGORIES.everyday].map((c) => (
                  <button
                    key={c.id}
                    data-testid={`catalogue-toggle-${c.id}`}
                    onClick={() => setCategory(c.id)}
                    className="relative rounded-full px-3.5 sm:px-5 py-2 font-body text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.22em] transition-colors duration-300"
                  >
                    {category === c.id && (
                      <motion.span
                        layoutId="catalogue-toggle-pill"
                        transition={{ type: "spring", stiffness: 350, damping: 32 }}
                        className="absolute inset-0 -z-10 rounded-full bg-white"
                      />
                    )}
                    <span className={category === c.id ? "text-black" : "text-white/70"}>
                      <span className="sm:hidden">{c.id === "onam" ? "Onam" : c.id === "bloom" ? "Bloom" : "Everyday"}</span>
                      <span className="hidden sm:inline">{c.label}</span>
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3 print:hidden">
                <button
                  data-testid="catalogue-share"
                  onClick={share}
                  className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 font-body text-[12px] uppercase tracking-[0.24em] text-black transition-colors duration-300 hover:bg-[var(--kln-gold)]"
                >
                  <Share2 className="h-4 w-4" strokeWidth={1.4} />
                  Share catalogue
                </button>
                <button
                  data-testid="catalogue-download-cover"
                  onClick={downloadCover}
                  className="inline-flex items-center gap-3 rounded-full border border-white/40 px-7 py-3.5 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                  <Download className="h-4 w-4" strokeWidth={1.4} />
                  Download cover
                </button>
                <a
                  data-testid="catalogue-order-all-wa"
                  href={waLink(buildGeneralMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-3.5 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px]"
                >
                  <WhatsAppMark className="h-4 w-4" />
                  WhatsApp us
                </a>
                <a
                  data-testid="catalogue-order-all"
                  href={IG_DM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-full border border-white/40 px-7 py-3.5 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                  Instagram DM
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.4} />
                </a>
              </div>
              <ReplyNudge testid="catalogue-wa-nudge" className="mt-3" />
            </div>

            {/* shareable cover poster */}
            <div className="lg:col-span-5">
              <Reveal delay={0.2}>
                <button
                  data-testid="catalogue-cover-poster"
                  onClick={downloadCover}
                  className="group relative mx-auto block w-full max-w-[340px] overflow-hidden border border-white/15 print:hidden"
                >
                  <img
                    src={`/catalogue-cover-${category}.jpg`}
                    alt={`Keilani ${cat.label} — shareable cover`}
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/80 to-transparent py-5 font-body text-[11px] uppercase tracking-[0.24em] text-white opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <Download className="h-4 w-4" strokeWidth={1.4} />
                    Tap to download
                  </span>
                </button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* items */}
      <section className="mx-auto max-w-[1300px] px-6 md:px-12 py-10 md:py-16">
        {items.map((p, i) => (
          <CatalogueItem key={p.id} product={p} i={i} />
        ))}
      </section>

      {/* order strip */}
      <section className="relative overflow-hidden bg-[#FDFBF7] border-t border-black/10">
        <div className="mx-auto max-w-[1300px] px-6 md:px-12 py-20 text-center">
          <p className="font-body text-[11px] uppercase tracking-[0.4em] text-black/40">
            To Order
          </p>
          <h2 className="mx-auto mt-5 max-w-2xl font-serif-editorial text-3xl md:text-5xl font-light italic leading-tight">
            Message us the piece and your size — we&apos;ll take it from there.
          </h2>
          <p className="mt-5 font-body text-[11px] uppercase tracking-[0.2em] text-black/45">
            Shipping all over the world · Custom sizes · {BRAND.whatsappNumber} · {BRAND.instagramHandle}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 print:hidden">
            <a
              data-testid="catalogue-footer-whatsapp"
              href={waLink(buildGeneralMessage())}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px]"
            >
              <WhatsAppMark className="h-4 w-4" />
              WhatsApp us
            </a>
            <a
              data-testid="catalogue-footer-dm"
              href={IG_DM}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-[#050505] px-8 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-[var(--kln-gold)] hover:text-black"
            >
              Instagram DM
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
            </a>
          </div>
          <div className="mt-4 flex justify-center">
            <ReplyNudge testid="catalogue-footer-wa-nudge" />
          </div>
        </div>
      </section>
    </div>
  );
}
